import React, { useMemo, useState } from 'react';
import { ChevronRight, Ghost, Home, Layers, Search, Star, Zap, Compass, Share2 } from 'lucide-react';
import DetailView from './DetailView';
import { stemsData } from '../data/stems';
import { stemCombinationsData } from '../data/stemCombinations';
import { doorsData } from '../data/doors';
import { starsData } from '../data/stars';
import { godsData } from '../data/gods';
import { trigramsData } from '../data/trigrams';
import { CategoryType, SymbolData } from '../types';
import { ReaderSettingsMenu, useReaderDisplaySettings } from './ReaderDisplaySettings';
import XuanYuanTopBar from './XuanYuanTopBar';

type XiangyiCategoryId = Extract<CategoryType, 'stems' | 'stem_combinations' | 'doors' | 'stars' | 'gods' | 'trigrams'>;

interface XiangyiCategoryConfig {
  id: XiangyiCategoryId;
  label: string;
  icon: React.ElementType;
  color: string;
  pro?: boolean;
}

const XIANGYI_CATEGORIES: XiangyiCategoryConfig[] = [
  { id: 'stems', label: '十天干', icon: Zap, color: 'text-indigo-600 bg-indigo-50' },
  { id: 'stem_combinations', label: '天干组合', icon: Layers, color: 'text-pink-600 bg-pink-50' },
  { id: 'doors', label: '八门', icon: Home, color: 'text-emerald-600 bg-emerald-50', pro: true },
  { id: 'stars', label: '九星', icon: Star, color: 'text-amber-600 bg-amber-50', pro: true },
  { id: 'gods', label: '八神', icon: Ghost, color: 'text-rose-600 bg-rose-50', pro: true },
  { id: 'trigrams', label: '八卦', icon: Compass, color: 'text-cyan-600 bg-cyan-50', pro: true },
];

const getCategoryData = (id: XiangyiCategoryId): SymbolData[] => {
  switch (id) {
    case 'stems':
      return stemsData;
    case 'stem_combinations':
      return stemCombinationsData;
    case 'doors':
      return doorsData;
    case 'stars':
      return starsData;
    case 'gods':
      return godsData;
    case 'trigrams':
      return trigramsData;
    default:
      return [];
  }
};

interface OriginMemoryReaderProps {
  onBack: () => void;
  canAccessPro: boolean;
  proCategoryUnlocks?: Partial<Record<XiangyiCategoryId, boolean>>;
  onRequirePro?: (toolName?: string) => void;
}

const OriginMemoryReader: React.FC<OriginMemoryReaderProps> = ({
  onBack,
  canAccessPro,
  proCategoryUnlocks,
  onRequirePro,
}) => {
  const [activeCategory, setActiveCategory] = useState<XiangyiCategoryId | null>(null);
  const [selectedSymbol, setSelectedSymbol] = useState<SymbolData | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [shareNotice, setShareNotice] = useState('');
  const { settings, update, reset } = useReaderDisplaySettings('xiangyi-reader', {
    paragraphSpacing: 14,
  });

  const handleShareCatalog = async () => {
    const url = new URL('/origin-memory', window.location.origin).toString();
    try {
      if (navigator.share) {
        await navigator.share({ title: '轩辕国学 · 象意速记', url });
        return;
      }
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setShareNotice('链接已复制，可直接分享。');
        window.setTimeout(() => setShareNotice(''), 2200);
        return;
      }
      setShareNotice(url);
      window.setTimeout(() => setShareNotice(''), 3200);
    } catch {
      setShareNotice('分享失败，请稍后重试。');
      window.setTimeout(() => setShareNotice(''), 2200);
    }
  };

  const allCategoryItems = useMemo<SymbolData[]>(
    () => (activeCategory ? getCategoryData(activeCategory) : []),
    [activeCategory],
  );

  const filteredItems = useMemo<SymbolData[]>(() => {
    const query = String(searchQuery || '').trim().toLowerCase();
    if (!query) return allCategoryItems;

    const textIncludes = (value: unknown): boolean => String(value || '').toLowerCase().includes(query);

    return allCategoryItems.filter((item) => {
      const basicHit = (
        textIncludes(item.summary) ||
        textIncludes(item.char) ||
        textIncludes(item.pinyin) ||
        ((item.tags || []).some((tag) => textIncludes(tag)))
      );
      if (basicHit) return true;

      // 支持按组合标题/象意正文检索（如“乙+辛”“青龙逃走”等）
      return (item.coreOrigins || []).some((origin) => (
        textIncludes(origin.title) ||
        textIncludes(origin.logic) ||
        (origin.subItems || []).some((sub) => (
          textIncludes(sub.label) || textIncludes(sub.content)
        ))
      ));
    });
  }, [allCategoryItems, searchQuery]);

  const canAccessCategory = (cat: XiangyiCategoryConfig): boolean => {
    if (!cat.pro) return true;
    if (canAccessPro) return true;
    return Boolean(proCategoryUnlocks?.[cat.id]);
  };

  const openCategory = (cat: XiangyiCategoryConfig) => {
    if (!canAccessCategory(cat)) {
      if (onRequirePro) onRequirePro(cat.label);
      return;
    }
    setActiveCategory(cat.id);
  };

  if (selectedSymbol) {
    const currentIndex = allCategoryItems.findIndex((item) => item.id === selectedSymbol.id);
    const hasNext = currentIndex !== -1 && currentIndex < allCategoryItems.length - 1;
    const hasPrev = currentIndex > 0;
    return (
      <DetailView
        data={selectedSymbol}
        onBack={() => setSelectedSymbol(null)}
        onNextSymbol={hasNext ? () => setSelectedSymbol(allCategoryItems[currentIndex + 1]) : undefined}
        onPrevSymbol={hasPrev ? () => setSelectedSymbol(allCategoryItems[currentIndex - 1]) : undefined}
        displaySettings={settings}
        onDisplaySettingsChange={update}
        onDisplaySettingsReset={reset}
      />
    );
  }

  if (activeCategory) {
    const currentCategory = XIANGYI_CATEGORIES.find((cat) => cat.id === activeCategory);
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <XuanYuanTopBar
          title={`轩辕国学·${currentCategory?.label || '象意速记'}`}
          onBack={() => {
            setActiveCategory(null);
            setSearchQuery('');
          }}
          variant="light"
          right={(
            <ReaderSettingsMenu
              settings={settings}
              onChange={update}
              onReset={reset}
              buttonClassName="border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
            />
          )}
        />

        <div className="flex-1 overflow-y-auto bg-slate-50 p-4 lg:p-8">
          <div className="max-w-5xl mx-auto pb-10">
            <div className="relative mb-6">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="搜索符号或组合（如 乙+辛）..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none shadow-sm"
              />
            </div>

            {filteredItems.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {filteredItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setSelectedSymbol(item)}
                    className="group flex flex-col items-start p-2.5 sm:p-3.5 lg:p-4 bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all text-left relative overflow-hidden"
                  >
                    <div className="flex items-center justify-between w-full mb-1.5 lg:mb-2">
                      <div className="w-7 h-7 lg:w-9 lg:h-9 rounded-lg bg-slate-900 text-amber-50 flex items-center justify-center font-serif text-base lg:text-lg shadow-md group-hover:scale-110 transition-transform z-10">
                        {item.char.charAt(0)}
                      </div>
                      <ChevronRight className="w-4 h-4 lg:w-5 lg:h-5 text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-1 transition-all" />
                    </div>
                    <h3 className="font-bold text-slate-900 mb-0.5 z-10" style={{ fontSize: `${Math.max(14, settings.fontSize)}px`, lineHeight: settings.lineHeight }}>{item.pinyin}</h3>
                    <p className="text-slate-500 line-clamp-2 z-10" style={{ fontSize: `${Math.max(12, settings.fontSize - 2)}px`, lineHeight: settings.lineHeight }}>{item.summary}</p>
                  </button>
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
                没有匹配到结果，请换个关键词。
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <XuanYuanTopBar
        title="轩辕国学·象意速记"
        onBack={onBack}
        subtitle={shareNotice || '点击卡片进入对应象意内容'}
        variant="light"
        right={(
          <div className="flex items-center gap-2">
            <button
              onClick={() => { void handleShareCatalog(); }}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
              aria-label="分享象意速记目录"
            >
              <Share2 className="h-4 w-4" />
              <span className="hidden sm:inline">分享</span>
            </button>
            <ReaderSettingsMenu
              settings={settings}
              onChange={update}
              onReset={reset}
              buttonClassName="border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
            />
          </div>
        )}
      />

      <div className="flex-1 overflow-y-auto p-4 lg:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6">
            {XIANGYI_CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              const unlocked = canAccessCategory(cat);
              return (
                <button
                  key={cat.id}
                  onClick={() => openCategory(cat)}
                  className={`flex flex-col items-start justify-between p-4 bg-white border border-slate-200 rounded-xl shadow-sm transition-all group h-36 md:h-44 lg:h-40 relative overflow-hidden hover:shadow-md hover:border-indigo-300 ${cat.pro && !unlocked ? 'opacity-90' : ''}`}
                >
                  {cat.pro && !unlocked && (
                    <span className="absolute top-2 right-2 rounded bg-amber-100 text-amber-700 px-1.5 py-0.5 text-[10px] font-bold">
                      PRO
                    </span>
                  )}
                  <Icon className="absolute -bottom-4 -right-4 w-16 h-16 md:w-20 md:h-20 opacity-5 group-hover:opacity-10 transition-opacity text-slate-900 pointer-events-none" />
                  <div className="flex justify-between w-full">
                    <div className={`w-10 h-10 rounded-xl ${cat.color} flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <ChevronRight className="w-5 h-5 text-slate-300" />
                    </div>
                  </div>
                  <span className="font-bold text-slate-800 text-base md:text-lg">{cat.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OriginMemoryReader;
